<template>
	<section class="rewarded-popup" :data-event-type="attributes?.type">
		<Tooltip
			:heading="translate(tooltipContent.title)"
			customWidth="16rem"
			class="rewarded-popup__tooltip"
			:disabled="!tooltipContent.text || !tooltipContent.title"
		>
			<template #element>
				<div
					class="rewarded-popup__icon event"
					v-if="currentOffer?.type === RewardType.EVENTIUM"
				>
					<span :class="`icon-${currentOffer?.icon}-56`" />
				</div>
				<div
					v-else
					class="rewarded-popup__icon"
					:class="`ad-reward-${currentOffer?.type}-80`"
				/>
			</template>
			<template #tooltip>
				<span class="rewarded-popup__tooltip-text">
					{{
						translate(tooltipContent.text).replace("{number}", "10")
					}}
				</span>
			</template>
		</Tooltip>
		<div class="rewarded-popup__text" v-html="rewardText" />
		<AppTextButton
			class="rewarded-popup__btn"
			:type="StatusTypes.PRIMARY"
			:text="translate('button.watch_ad').toUpperCase()"
			@click="watchAd"
		/>
		<AppTextButton
			class="rewarded-popup__btn"
			:type="StatusTypes.DEFAULT"
			:text="translate('button.discard').toUpperCase()"
			@click="rejectOffer"
		/>
		<div class="rewarded-popup__btn-divider">
			<DecorativeTextDivider :label="translate('ui.or')" />
		</div>
		<AppTextButton
			class="chest-popup__btn"
			:type="
				resourcesStore.adSkipper
					? StatusTypes.SUCCESS
					: StatusTypes.WARNING
			"
			:text="
				translate(
					resourcesStore.adSkipper
						? 'button.skip_ad'
						: `button.buy_${ResourceType.AD_SKIPPER}`,
				)
			"
			@click="useAdSkipper"
		/>
		<div class="rewarded-popup__skippers">
			{{ `${translate(`ui.${ResourceType.AD_SKIPPER}_left`)}: ` }}
			<span>{{ formatNumber(resourcesStore.adSkipper) }}</span>
			<span :class="`icon-${ResourceType.AD_SKIPPER}-16`" />
		</div>
	</section>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import DecorativeTextDivider from "@/components/SVG/DecorativeTextDivider.vue";
import Tooltip from "@/components/Tooltip.vue";
import { useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import { ToastPlacement } from "@/game/ToastMessage";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import { AdAnalyticsClickSource, AdAnalyticsType } from "@/libs/SocketService";
import router from "@/router";
import useOfferStore, { RewardType } from "@/store/useOfferStore";
import useResourcesStore from "@/store/useResourcesStore";
import { RouteNames, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { iconClassName } from "@/utils/iconClassName";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";

const claimGuard = useRewardClaimGuard();

const communicator = markRaw(Communicator.getInstance());
const socketService = markRaw(useSocketService());
const resourcesStore = useResourcesStore();

const offerStore = useOfferStore();
const { currentOffer } = storeToRefs(offerStore);

const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

const rewardText = computed((): string => {
	const rewardIcon = `<span class='rewarded-popup__reward-icon ${iconClassName(currentOffer.value?.icon ?? ResourceType.COINS, 16)}'></span>`;

	let text = "ui.watch_ad_to_get_rescources";

	if (isChest.value) text = "ui.watch_ad_chest";
	if (isSuperClicker.value) text = "ui.watch_ad_superclicker";
	if (isClicker.value) text = "ui.watch_ad_multiclicker";

	return translate(text)
		.replace(
			"{number}",
			`<span>${formatNumber(currentOffer.value?.amount ?? 0)}</span>`,
		)
		.replace("{icon}", rewardIcon);
});

const isChest = computed(
	(): boolean => currentOffer?.value?.type === RewardType.CHESTS,
);
const isGems = computed(
	(): boolean => currentOffer?.value?.type === RewardType.GEMS,
);
const isCoins = computed(
	(): boolean => currentOffer?.value?.type === RewardType.COINS,
);
const isSuperClicker = computed(
	(): boolean => currentOffer?.value?.type === RewardType.SUPER_CLICKERS,
);
const isClicker = computed((): boolean => {
	if (!currentOffer?.value) return false;
	const clickers = [
		RewardType.CLICKER_2X,
		RewardType.CLICKER_3X,
		RewardType.CLICKER_5X,
	];
	return clickers.includes(currentOffer.value.type);
});

const tooltipContent = computed((): { title: string; text: string } => {
	let title = "";
	let text = "";

	if (isGems.value) {
		title = "resource.gems";
		text = "resource.gems_description";
	}
	if (isCoins.value) {
		title = "resource.coins";
		text = "resource.coins_description";
	}
	if (isSuperClicker.value) {
		title = "resource.superClicker";
		text = "resource.superClicker_description";
	}
	if (isClicker.value) {
		title = "resource.click_multiplier";
		text = "ui.click_multiplier_description";
	}
	return { title, text };
});

const close = (): void => {
	offerStore.setVisibility(false);
};

const watchAd = (): Promise<void> =>
	claimGuard.guard((): void => {
		if (import.meta.env.DEV) {
			offerStore.acceptOffer();
			close();
			window.rewardedVideoEarned();
			return;
		}
		offerStore.acceptOffer();

		socketService.emitAdAnalyticsSave({
			type: AdAnalyticsType.REWARDED_AD,
			clickSource: AdAnalyticsClickSource.POPUP,
			offerType: offerStore.currentOffer?.type,
		});
		communicator.playRewardedAd();
		close();
	});

const rejectOffer = (): void => {
	offerStore.rejectOffer();
	close();
};

const useAdSkipper = (): Promise<void> =>
	claimGuard.guard((): void => {
		if (resourcesStore.adSkipper <= 0) {
			router.push({
				name: RouteNames.SHOP,
				params: { scrollTo: ResourceType.AD_SKIPPER },
			});
			close();
			return;
		}

		resourcesStore.decrease(ResourceType.AD_SKIPPER, 1, "used for offer");
		offerStore.acceptOffer();

		const rewardIcon = offerStore.currentOffer?.icon.includes("clicker")
			? ResourceType.SUPER_CLICKER.toLowerCase()
			: offerStore.currentOffer?.icon;

		const reward = {
			[rewardIcon as string]: offerStore.currentOffer?.amount,
		};
		offerStore.applyReward();

		setTimeout((): void => {
			useEmitter().emit(EmitterEvents.PhaserTileClick, {
				x: 0,
				y: 0,
				reward,
				placement: ToastPlacement.TOP_CENTER,
			});
		}, 100);
		close();
	});
</script>

<style lang="scss" scoped>
.rewarded-popup {
	padding: 0.625rem 0.25rem;
	@include flex-col-center;
	gap: 0.5rem;
	&__icon {
		position: fixed;
		top: 4rem;
		&.event {
			background-image: url($path-events + "christmas/backgrounds/ad-reward-bg.avif");
			background-size: contain;
			background-repeat: no-repeat;
			width: 5rem;
			height: 5rem;
			@include flex-row-center;
		}
	}

	&__tooltip {
		position: fixed;
		top: 4rem;
		.rewarded-popup__icon {
			position: static;
		}
		&-text {
			text-align: center;
		}
	}
	&__text {
		margin-top: 2rem;
		color: #372615;
		font-size: 0.906rem;
		text-align: center;
		& > span {
			font-weight: bold;
		}
	}
	&__btn {
		width: 15.625rem;
		&-divider {
			& > span {
				color: #372615;
				font-weight: bold;
				font-size: 1.063rem;
			}
		}
	}
	&__skippers {
		display: flex;
		align-items: center;
		gap: 0.15rem;
		color: #372615;
		font-size: 0.906rem;
		& > span {
			font-weight: bold;
		}
	}
	:deep(.rewarded-popup__text > span) {
		font-weight: bold;
	}
	:deep(.rewarded-popup__reward-icon) {
		position: relative;
		top: 0.1rem;
	}
	&[data-event-type="fishing"] {
		.rewarded-popup__icon.event {
			background-image: url($path-events + "fishing/backgrounds/ad-reward-bg.avif");
		}
	}
	&[data-event-type="solarfair"] {
		.rewarded-popup__icon.event {
			background-image: url($path-events + "solarfair/backgrounds/ad-reward-bg.avif");
		}
	}
	&[data-event-type="hunting"] {
		.rewarded-popup__icon.event {
			background-image: url($path-events + "hunting/backgrounds/ad-reward-bg.avif");
		}
	}
	&[data-event-type="halloween"] {
		.rewarded-popup__icon.event {
			background-image: url($path-events + "halloween/backgrounds/ad-reward-bg.avif");
		}
	}
	&[data-event-type="pirates"] {
		.rewarded-popup__icon.event {
			background-image: url($path-events + "pirates/backgrounds/ad-reward-bg.avif");
		}
	}
}
</style>
